import { useMemo } from "react";
import { number, object, oneOf, node, bool } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { getPath } from "mobx-state-tree";
import { useDraggable } from "@dnd-kit/react";
import { Collapse, Grid, IconButton, Tooltip } from "@material-ui/core";
import {
  useEstimateEditorSettings,
  useEstimateStructure,
  useTableExpandHandler,
} from "@tools";
import { getTimelineSectionsChartBar } from "@tools/EstimateEditor/utils";
import { TimelineSectionChartBar } from "./TimelineSectionChartBar";
import { TimelineDistributionCells } from "./TimelineDistributionCells";
import { ViewWeekOutlined } from "@material-ui/icons";
import useCellStyle from "@tools/EstimateEditor/styles/CommonTableCellStyles.style";
import classnames from "classnames";

export const TimelineSectionChartContainer = observer(({
  index,
  element,
  type,
  expandable,
  editable,
  children,
  height,
  useValueMode,
  blocksStartDate,
  labelActions,
  topTransferContainer,
  bottomTransferContainer,
  rightActions,
  expandOnContainerClick,
  blurOnExpand,
}) => {
  const { t } = useTranslation();
  const { allowEdition, blockExpansions, isPdfPrinterView } = useEstimateEditorSettings();
  const structure = useEstimateStructure();
  const cellClasses = useCellStyle();

  const valueMode = Boolean(element.valueMode);
  const canEditElement = allowEdition && !element.isAutomated;

  const { maxTimelineEndDate, timelineExpandedMonths, timelineStartDate } = structure.settings;
  const { expanded: elementExpanded } = element;

  const path = getPath(element);

  const expanded = useMemo(() => (
    expandable && (elementExpanded || blockExpansions)
  ), [ blockExpansions, expandable, elementExpanded ]);
  const handleValueMode = (e) => {
    e.stopPropagation();
    element.setValueMode(!valueMode);
  };

  const { handleOpen, ...sectionCollapseProps } =
    useTableExpandHandler({
      expandable: expandable,
      isOpened: elementExpanded,
      onExpand: element.setExpanded,
    });

  const {
    barLeft,
    unmodifiedWidth,
    offsetLeft,
    offsetRight,
    ...barProps
  } = getTimelineSectionsChartBar({
    element,
    blocksStartDate,
    timelineStartDate,
    timelineExpandedMonths,
    maxTimelineEndDate,
    height,
  });

  const { ref, handleRef } = useDraggable({
    id: element.id,
    index,
    type,
    data: { path, action: "move" },
    disabled: !canEditElement || barProps.barWidth <= 0,
    alignment: { x: "start", y: "center" },
    attributes: {
      tabIndex: index,
      "aria-label": "Move element on timeline",
    }
  });
  return (
    <Grid
      ref={ref}
      item container
      direction="column"
      wrap="nowrap"
      alignItems="flex-start"
      className="h-max w-max noDrag ignore-events relative"
      style={{
        left: barLeft,
        color: element.fontColor,
        opacity: element.isTurnedOff ? 0.3 : undefined,
      }}
    >
      { topTransferContainer }
      <Grid
        item container
        wrap="nowrap"
        alignItems="center"
        className={height === "large" ? cellClasses.size2xLarge : cellClasses.sizeMedium}
      >
        <TimelineSectionChartBar
          ref={handleRef}
          key={element.id}
          id={element.id}
          index={index}
          path={path}
          type={type}
          {...barProps}
          expandable={expandable}
          expanded={expanded}
          onExpand={handleOpen}
          onClick={useValueMode ? handleValueMode : undefined}
          labelActions={labelActions}
          hideLabel={valueMode}
          expandOnContainerClick={expandOnContainerClick}
          editable={canEditElement}
          style={expanded && blurOnExpand ? { opacity: 0.6 } : undefined}
        >
          <>
          {
            useValueMode && valueMode &&
            <TimelineDistributionCells
              element={element}
              allowEdition={allowEdition && editable}
              expandedMonths={timelineExpandedMonths}
              unmodifiedWidth={unmodifiedWidth}
              offsetLeft={offsetLeft}
              offsetRight={offsetRight}
              backgroundColor={barProps.valueColor}
              barWidth={barProps.barWidth}
            />
          }
          {
            !isPdfPrinterView && useValueMode && barProps.barWidth > 1 &&
            <Tooltip title={t(`views.editor.timeline_table.bar_values.${valueMode ? "hide" : "show"}`)}>
              <IconButton
                color="primary"
                size="small"
                onClick={handleValueMode}
                className={classnames(
                  "absolute noDrag p-0-5 text-sm apply-events",
                  !valueMode && "hoverIcon"
                )}
                style={{ right: -24 }}
              >
                <ViewWeekOutlined />
              </IconButton>
            </Tooltip>
          }
          {rightActions}
          </>
        </TimelineSectionChartBar>
      </Grid>
      { bottomTransferContainer }
      {
        expandable &&
        <Collapse
          in={expanded}
          timeout="auto"
          mountOnEnter
          unmountOnExit
          {...sectionCollapseProps}
        >
          <Grid item container direction="column" alignItems="flex-start">
            {children}
          </Grid>
        </Collapse>
      }
    </Grid>
  );
});

TimelineSectionChartContainer.propTypes = {
  element: object.isRequired,
  index: number.isRequired,
  height: oneOf(["large", "medium", "small", "weight"]),
  type: oneOf(["section", "workType", "teamMember"]).isRequired,
  children: node,
  expandable: bool,
  editable: bool,
  useValueMode: bool,
  labelActions: node,
  blocksStartDate: object,
  topTransferContainer: node,
  bottomTransferContainer: node,
  rightActions: node,
  blurOnExpand: bool,
  expandOnContainerClick: bool,
};
